/**
 * L2: Home tab (01-references §6 S2). It copies the 1Password iOS Home: customizable sections
 * (Favorites, Recently Used, Pinned Fields) with a Customize control. A pinned field copies on
 * tap and never shows its value. Its cell is a copy control (data-copy), so the tap writes the
 * clipboard once.
 */
import type { AppActions, AppState } from "../contract.js";
import { accountButton, cell, emptyState, fakeChip, group, navBar, sheetFrame } from "../shell/chrome.js";
import { avatar, h, icon } from "../shell/dom.js";
import { otpNow } from "../detail/otp.js";
import { HOME_SECTION_TITLE, homeModel, moveSection, toggleSection, type Pin } from "../vm/home.js";
import { isFavorite } from "../vm/items.js";
import { itemRow } from "./row.js";

function pinCell(pin: Pin, state: AppState, actions: AppActions): HTMLElement {
  const masked = pin.field.kind === "otp" ? otpNow(pin.field, state.now).grouped : pin.field.sensitive ? "••••••••" : pin.field.value;
  return cell({
    label: `${pin.item.title} · ${pin.field.label}`,
    lead: avatar(pin.item.title),
    detail: h("span", { class: pin.field.kind === "otp" ? "cell-detail otp-code" : "cell-detail" }, masked),
    accessory: h("span", { class: "cell-accessory" }, icon("copy", "icon small")),
    onClick: () => actions.copyField(pin.ref),
    copy: true,
  });
}

export function renderHome(state: AppState, actions: AppActions): HTMLElement {
  const snap = state.snapshot!;
  const m = homeModel(snap, state.homePrefs, state.pinned, state.favoriteOverrides, state.scope);
  const header = navBar({
    left: accountButton(state, actions),
    right: h("button", { class: "navbar-btn", onclick: () => actions.openSheet({ kind: "customize-home" }) }, "Customize"),
    largeTitle: "Home",
    largeTitleAccessory: snap.fixture ? fakeChip() : null,
  });

  const sections = m.sections.map((s) => {
    if (s.pins) {
      return group(
        s.title,
        s.pins.length ? s.pins.map((p) => pinCell(p, state, actions)) : [cell({ label: "Pin a field from any item to copy it from here.", cls: "disabled" })],
        { foot: s.pins.length ? "Tap a pinned field to copy it." : undefined },
      );
    }
    const items = s.items ?? [];
    return group(
      s.title,
      items.length
        ? items.map((i) => itemRow(i, snap.wallets, actions, { showWallet: state.scope === null, favorite: isFavorite(i, state.favoriteOverrides) }))
        : [cell({ label: s.id === "favorites" ? "Mark items as favorites to see them here." : "Items you open will show up here.", cls: "disabled" })],
      { action: s.id === "favorites" && items.length ? h("button", { class: "navbar-btn", onclick: () => actions.navigate({ name: "items", list: "favorites" }) }, "See All") : undefined },
    );
  });

  const body = m.sections.length === 0 ? emptyState("home", "Home is empty", "Use Customize to choose what Home shows.") : h("div", {}, ...sections);
  return h("main", { class: "screen", "data-screen": "home" }, header, body);
}

export function renderCustomizeHomeSheet(state: AppState, actions: AppActions): HTMLElement {
  const prefs = state.homePrefs;
  return sheetFrame(
    "Customize Home",
    actions,
    h("p", { class: "sheet-note" }, "Choose which sections Home shows, and their order."),
    group(
      null,
      prefs.sections.map((s, i) => {
        const toggle = h("input", {
          type: "checkbox",
          class: "switch",
          role: "switch",
          "aria-label": `Show ${HOME_SECTION_TITLE[s.id]}`,
          checked: s.visible,
          onchange: () => actions.setHomePrefs(toggleSection(prefs, s.id)),
        });
        return cell({
          label: HOME_SECTION_TITLE[s.id],
          accessory: h(
            "span",
            { class: "reorder" },
            h("button", { class: "icon-btn", "aria-label": `Move ${HOME_SECTION_TITLE[s.id]} up`, disabled: i === 0, onclick: () => actions.setHomePrefs(moveSection(prefs, s.id, -1)) }, "↑"),
            h("button", { class: "icon-btn", "aria-label": `Move ${HOME_SECTION_TITLE[s.id]} down`, disabled: i === prefs.sections.length - 1, onclick: () => actions.setHomePrefs(moveSection(prefs, s.id, 1)) }, "↓"),
            toggle,
          ),
        });
      }),
    ),
  );
}

